import type { VideoEditInstruction } from '@/lib/videoEditPrompt';

import type { Seedance25EditTier } from '@/constants/videoModels';

/**
 * What "Edit again" carries into the Edit Video tool for an edit of an
 * uploaded clip. The tool is reached by URL (?tab=edit-video&video=…), which
 * has no room for the cues and reference images, so they ride in
 * sessionStorage. Without them the user had to retype the edit, and copying
 * the gallery's composed prompt instead is what corrupted row 5429482.
 */
export interface VideoEditSeed {
  videoUrl: string;
  instructions?: VideoEditInstruction[];
  referenceImages?: string[];
  tier?: Seedance25EditTier;
  resolution?: string;
}

const STORAGE_KEY = 'edit-video-seed';
const MAX_AGE_MS = 10 * 60 * 1000;

export function saveVideoEditSeed(seed: VideoEditSeed): void {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...seed, savedAt: Date.now() }),
    );
  } catch {
    // Storage can be unavailable (private mode); the tool still opens.
  }
}

/** Returns the seed saved for this clip (once) and forgets it. */
export function takeVideoEditSeed(videoUrl: string): VideoEditSeed | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const seed = JSON.parse(raw);
    if (seed?.videoUrl !== videoUrl) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    if (Date.now() - Number(seed.savedAt || 0) > MAX_AGE_MS) return null;
    return {
      videoUrl,
      instructions: Array.isArray(seed.instructions)
        ? seed.instructions
        : undefined,
      referenceImages: Array.isArray(seed.referenceImages)
        ? seed.referenceImages.filter(
            (url: unknown): url is string => typeof url === 'string',
          )
        : undefined,
      tier: seed.tier,
      resolution: seed.resolution,
    };
  } catch {
    return null;
  }
}
